import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { catchError, forkJoin, of } from 'rxjs';
import { getLocalizedValue, GlobalMessageUtil } from '@card-software/nexus-utilities';
import {
  MonthlyCostProxyService,
  MonthlyCostRunStatus,
  MonthlyCostSummaryDto,
  PrerequisiteCheckResult,
} from '../../../proxies/monthly-cost.proxy.service';
import { ALL_COST_CENTERS, CalcRowState, CalcRowStatus, CostCenterInfo } from '../models/cost-analysis.models';

interface CalculationCompleted {
  anySuccess: boolean;
}

/**
 * Calculation Control Panel — drives /api/v1/monthly-cost/{prerequisites,calculate}.
 *
 * Lives above the Cost Analysis dashboards on the main page. Lets the user
 * (1) verify per-cost-center prerequisites, (2) trigger a full month close
 * via /calculate (with empty <c>costCenterCodes</c>) for the selected period,
 * and (3) inspect per-area outcomes (success/failure with error code/message).
 *
 * On a successful run the panel emits `calculationCompleted` so the parent
 * can refresh KPIs / hierarchy without forcing the user to re-load.
 */
@Component({
  selector: 'app-calculation-control',
  templateUrl: './calculation-control.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalculationControlComponent {
  private readonly proxy = inject(MonthlyCostProxyService);
  private readonly translate = inject(TranslateService);

  readonly year = input.required<number>();
  readonly month = input.required<number>();
  /** Auto-expand the panel when the parent reports the period is uncalculated. */
  readonly autoExpand = input<boolean>(false);

  readonly calculationCompleted = output<CalculationCompleted>();

  // ── Form ───────────────────────────────────────────────────────────────────
  readonly exchangeRateControl = new FormControl<number | null>(null, [Validators.required, Validators.min(0.0001)]);
  readonly exchangeRateEurUsdControl = new FormControl<number>(0, [Validators.required, Validators.min(0)]);
  // ── Local state ────────────────────────────────────────────────────────────
  readonly collapsed = signal<boolean>(true);
  readonly checkingPrereqs = signal<boolean>(false);
  readonly running = signal<boolean>(false);
  readonly rows = signal<CalcRowState[]>(this.buildInitialRows());
  readonly lastCheckedAt = signal<Date | null>(null);
  readonly lastRunAt = signal<Date | null>(null);

  readonly anyBlocked = computed(() => this.rows().some((r) => r.status === 'blocked'));
  readonly hasResults = computed(() => this.rows().some((r) => r.status === 'success' || r.status === 'failed'));
  readonly anySuccess = computed(() => this.rows().some((r) => r.status === 'success'));
  readonly anyFailed = computed(() => this.rows().some((r) => r.status === 'failed'));
  readonly canCalculate = computed(
    () => !this.running() && !this.anyBlocked() && this.exchangeRateControl.valid && this.exchangeRateEurUsdControl.valid,
  );

  constructor() {
    // Reset rows when the period changes so a stale "Success" badge from a
    // previous month isn't carried over. allowSignalWrites is required because
    // we touch signals from inside an effect.
    effect(
      () => {
        // Subscribe to period changes.
        this.year();
        this.month();
        untracked(() => {
          this.rows.set(this.buildInitialRows());
          this.lastCheckedAt.set(null);
          this.lastRunAt.set(null);
          this.collapsed.set(!this.autoExpand());
        });
      },
      { allowSignalWrites: true },
    );

    // Sync collapsed state with autoExpand when only that input changes.
    effect(
      () => {
        const auto = this.autoExpand();
        untracked(() => {
          if (auto) {
            this.collapsed.set(false);
          }
        });
      },
      { allowSignalWrites: true },
    );
  }

  toggleCollapse(): void {
    this.collapsed.update((c) => !c);
  }

  // ── Prerequisites check ────────────────────────────────────────────────────
  checkPrerequisites(): void {
    const y = this.year();
    const m = this.month();
    if (this.checkingPrereqs() || this.running()) {
      return;
    }
    this.checkingPrereqs.set(true);
    this.rows.update((rs) => rs.map((r) => ({ ...r, status: 'checking' as CalcRowStatus, missingItems: [] })));

    const calls = ALL_COST_CENTERS.map((cc) =>
      this.proxy.checkPrerequisites(cc.code, y, m).pipe(
        catchError(() =>
          of({
            success: false,
            data: { canCalculate: false, missingItems: ['Service contact error.'] } as PrerequisiteCheckResult,
            errorMessage: null,
          }),
        ),
      ),
    );

    forkJoin(calls).subscribe({
      next: (responses) => {
        this.rows.update((rs) =>
          rs.map((row, idx) => {
            const data = responses[idx]?.data ?? { canCalculate: false, missingItems: ['No response'] };
            return {
              ...row,
              status: data.canCalculate ? 'ready' : 'blocked',
              missingItems: data.canCalculate ? [] : (data.missingItems ?? []),
            };
          }),
        );
        this.lastCheckedAt.set(new Date());
        this.checkingPrereqs.set(false);
      },
      error: () => {
        this.rows.update((rs) =>
          rs.map((row) => ({ ...row, status: 'blocked' as CalcRowStatus, missingItems: ['Service contact error.'] })),
        );
        this.checkingPrereqs.set(false);
      },
    });
  }

  // ── Calculate (full close) ─────────────────────────────────────────────────
  /**
   * Triggers a full month close via <c>POST /calculate</c> with an empty
   * <c>costCenterCodes</c> list — the API runs every registered area in
   * parallel plus the post-passes (SharedMoi and Waste cross-area).
   */
  calculateAll(): void {
    // TODO: check that they are seeded
    // if (!this.canCalculate()) {
    //   return;
    // }
    const y = this.year();
    const m = this.month();
    const exchangeRate = this.exchangeRateControl.value ?? 0;
    const exchangeRateEurUsd = this.exchangeRateEurUsdControl.value ?? 0;

    this.running.set(true);
    this.rows.update((rs) => rs.map((r) => ({ ...r, status: 'running' as CalcRowStatus })));

    this.proxy
      .calculate({
        year: y,
        month: m,
        exchangeRate,
        exchangeRateEurUsd,
        costCenterCodes: null, // null/empty → full close
      })
      .pipe(
        catchError((err) => {
          const message = getLocalizedValue(this.translate, err?.error?.errorMessage) || err?.message || 'Network error';
          this.rows.update((rs) =>
            rs.map((r) =>
              r.status === 'running'
                ? ({
                    ...r,
                    status: 'failed',
                    errorCode: 'NETWORK',
                    errorMessage: message,
                    lastUpdatedAt: new Date(),
                  } satisfies CalcRowState)
                : r,
            ),
          );
          this.running.set(false);
          return of(null);
        }),
      )
      .subscribe((response) => {
        if (response === null) {
          return;
        }
        const summaries = response.success && response.data ? response.data : [];
        this.applySummariesToRows(summaries);
        this.lastRunAt.set(new Date());
        this.running.set(false);

        const anySuccess = this.anySuccess();
        const anyFailed = this.anyFailed();
        if (anySuccess && !anyFailed) {
          GlobalMessageUtil.success('Cost Analysis', 'Calculation complete.');
        } else if (anySuccess && anyFailed) {
          GlobalMessageUtil.warn('Cost Analysis', 'Some areas failed. Review the panel for details.');
        } else if (anyFailed) {
          GlobalMessageUtil.error('Cost Analysis', 'Calculation failed for every area. Review the panel for details.');
        }

        this.calculationCompleted.emit({ anySuccess });
      });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  chipClasses(status: CalcRowStatus): string {
    const base = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium';
    switch (status) {
      case 'idle':
        return `${base} bg-[var(--p-surface-100)] text-[var(--p-surface-700)]`;
      case 'checking':
        return `${base} bg-[var(--p-sky-100)] text-[var(--p-sky-700)]`;
      case 'ready':
        return `${base} bg-[var(--p-green-100)] text-[var(--p-green-700)]`;
      case 'blocked':
        return `${base} bg-[var(--p-red-100)] text-[var(--p-red-700)]`;
      case 'queued':
      case 'running':
        return `${base} bg-[var(--p-orange-100)] text-[var(--p-orange-700)]`;
      case 'success':
        return `${base} bg-[var(--p-green-200)] text-[var(--p-green-800)]`;
      case 'failed':
        return `${base} bg-[var(--p-red-200)] text-[var(--p-red-800)]`;
      default:
        return base;
    }
  }

  chipIcon(status: CalcRowStatus): string {
    switch (status) {
      case 'idle':
        return 'pi pi-circle';
      case 'checking':
      case 'queued':
      case 'running':
        return 'pi pi-spin pi-spinner';
      case 'ready':
        return 'pi pi-check';
      case 'blocked':
        return 'pi pi-ban';
      case 'success':
        return 'pi pi-check-circle';
      case 'failed':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-circle';
    }
  }

  trackByCode(_index: number, row: CalcRowState): string {
    return row.costCenter.code;
  }

  private buildInitialRows(): CalcRowState[] {
    return ALL_COST_CENTERS.map((cc: CostCenterInfo) => ({
      costCenter: cc,
      status: 'idle' as CalcRowStatus,
      missingItems: [],
    }));
  }

  private applySummariesToRows(summaries: MonthlyCostSummaryDto[]): void {
    const byCode = new Map<string, MonthlyCostSummaryDto>();
    for (const s of summaries) {
      byCode.set(s.costCenterCode, s);
    }
    this.rows.update((rs) =>
      rs.map((row) => {
        const summary = byCode.get(row.costCenter.code);
        if (!summary) {
          // No response for this area — engine skipped it. Mark as failed
          // so it doesn't linger in 'running'.
          return {
            ...row,
            status: 'failed',
            errorCode: 'NO_RESPONSE',
            errorMessage: 'No result returned for this area.',
            lastUpdatedAt: new Date(),
          };
        }
        const ok = summary.status === MonthlyCostRunStatus.Success;
        return {
          ...row,
          status: ok ? 'success' : 'failed',
          rollsProcessed: summary.rollsProcessed,
          rollsUpdated: summary.rollsUpdated,
          totalCostUsd: summary.totalCostUsd,
          errorCode: ok ? undefined : (summary.errorCode ?? undefined),
          errorMessage: ok ? undefined : (summary.errorMessage ?? undefined),
          lastUpdatedAt: new Date(),
        };
      }),
    );
  }
}
